import { useState, useEffect, useMemo, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../../store/store';
import { getDependencyRecords, detectConflictChange } from '@mm-shared/dependencies';
import type { DependencyRecord } from '@mm-shared/dependencies';
import type { Conflict1, Status1 } from '@mm-shared/types';
import { updateObjective } from '../../store/map3PendingSlice';
import { updateStatusWithConflict, updateStatusWithResolvedConflict } from '../../utils/statusUtils';
import isEqual from 'lodash/isEqual';
import { cloneDeep, result, set } from 'lodash';

/**
 * Hook for detecting dependency conflicts in a milestone map.
 * Tracks changes in dependencies and detects when conflicts are created or resolved.
 * Automatically updates statuses when conflicts are detected.
 *
 * @param milestoneMapUuid - UUID of the milestone map to monitor
 * @param onConflictDetected - Optional callback when conflicts are detected, called after status updates
 * @returns Object containing current conflicts and related state
 */
export function useConflictDetection(
  milestoneMapUuid: string,
  onConflictDetected?: (conflicts: Conflict1[]) => void,
) {
  const dispatch = useDispatch<AppDispatch>();

  // Get required data from Redux
  const milestoneMap = useSelector((state: RootState) =>
    state.map3Data.milestoneMaps.find((map) => map.uuid === milestoneMapUuid),
  );
  const allObjectives = useSelector((state: RootState) => state.map3Data.objectives);
  const allMaps = useSelector((state: RootState) => state.map3Data.milestoneMaps);

  // Use a ref instead of state for last known dependencies
  const lastKnownDependenciesRef = useRef<DependencyRecord[]>([]);
  // Track previous objectives for more precise orphaned status reset
  const prevObjectivesRef = useRef<any[]>([]);

  // Get current dependencies
  const currentDependencies = useMemo(() => {
    if (!milestoneMap || !allObjectives || !allMaps) return [];
    return getDependencyRecords(milestoneMap, allObjectives, allMaps);
  }, [milestoneMap, allObjectives, allMaps]);

  // Compare current dependencies with last known state to detect changes
  // Every time currentDependencies changes, this runs, checks for conflicts,
  // and then updates lastKnownDependencies state.
  useEffect(() => {
    // Only run effect if dependencies have actually changed
    if (isEqual(lastKnownDependenciesRef.current, currentDependencies)) return;

    // Save the current dependencies immediately to break the loop
    const prevDependencies = lastKnownDependenciesRef.current;
    lastKnownDependenciesRef.current = currentDependencies;

    // Don't run on initial mount
    if (prevDependencies.length === 0) {
      // Save a deep copy of objectives for future comparison.
      // This is used to detect when orphaned statuses should be reset.
      // See comment below for more details. (BH 4/29/25)
      prevObjectivesRef.current = cloneDeep(allObjectives);
      return;
    }

    // Compare states to detect conflicts
    const { conflicts } = detectConflictChange(prevDependencies, currentDependencies);

    if (conflicts.length > 0) {
      console.log('[useConflictDetection] Detected conflicts:', conflicts);

      // Process each conflict and update statuses
      conflicts.forEach((conflict) => {
        // Find the relevant objective
        const objective = allObjectives.find(
          (obj) =>
            obj.uuid === conflict.partiesUuids.sourceObjective ||
            obj.uuid === conflict.partiesUuids.targetObjective,
        );
        if (!objective) return;

        // Find the relevant result
        const result = objective.results.find(
          (r) => r.uuid === conflict.partiesUuids.sourceResult,
          //TODO: Revisit. Should target result have status update or other  color indicator when it creates a problem for someone else?
          // || r.uuid === conflict.partiesUuids.targetResult,
        );
        if (!result) return;

        // Find or create status for this result's interval
        const existingStatus = objective.statuses.find(
          // TODO: This is probably fine for 90% of use cases. But probably good to harden this, in case status and result fall in
          // the same interval range, but don't have the same date stored. (E.g. future state with results setting more precise deadline dates,
          // or dependencies/conflicts across maps with different interval ranges or week ending days.)
          (s) => s.forIntervalEnding === (result.deadline || result.forIntervalEnding),
        );

        const baseStatus: Status1 = existingStatus || {
          uuid: crypto.randomUUID(),
          shortId: crypto.randomUUID().slice(0, 10),
          forIntervalEnding: result.deadline || result.forIntervalEnding,
          statusCode: '',
          createdDate: new Date().toISOString(),
          knownConflicts: [],
          resolvedConflicts: [],
        };

        // Update the status based on whether the conflict was resolved
        const updatedStatus = conflict.dateResolved
          ? updateStatusWithResolvedConflict(baseStatus, conflict)
          : updateStatusWithConflict(baseStatus, conflict);

        // Update the objective with the new status
        dispatch(
          updateObjective({
            uuid: objective.uuid,
            updates: {
              statuses: [
                ...objective.statuses.filter(
                  (s) => s.forIntervalEnding !== result.forIntervalEnding,
                ),
                updatedStatus,
              ],
            },
          }),
        );
      });

      // Call the callback after status updates if provided
      onConflictDetected?.(conflicts);
    }

    // TODO: Known bugs
    //  - wacky things happen with color coding auto-changes combined with multiple dependencies from one node to many.

    // --- Reset orphaned statuses (no results in interval) ---
    // TODO: This is a hack to reset orphaned statuses. It's good for a POC / demo.
    // But it's probably too aggressive. I can imagine people getting annoyed if this is
    // changing their colors on them. (BH 4/28/25)
    //
    // Attempting something a bit more precise below:
    // Statuses are only reset to 'p' if:
    // - The interval had a result in the previous state, and now has none.
    // - The status was set to 'r' (conflict) or had known conflicts.
    // - The interval is in the future.
    // - The status was red in the previous state.
    // Notes:
    // - This fixed overzealous edge (dependency) deletions creating
    //   rougue 'p' cells.
    // - Still buggy. Now there are cells that turn red that aren't turning
    //   back to gray again, which is what prompted this whole thing in the first place.
    //  (BH 4/29/25)
    allObjectives.forEach((objective) => {
      // Find the previous version of this objective
      const prevObjective = prevObjectivesRef.current.find((o: any) => o.uuid === objective.uuid);
      if (!prevObjective) return;
      objective.statuses.forEach((currStatus: Status1, index: number) => {
        const prevStatus = prevObjective.statuses[index];
        const prevResults = prevObjective.results.filter(
          (r: any) => (r.deadline || r.forIntervalEnding) === currStatus.forIntervalEnding,
        );
        const currResults = objective.results.filter(
          (r: any) => (r.deadline || r.forIntervalEnding) === currStatus.forIntervalEnding,
        );
        const isFuture = new Date(currStatus.forIntervalEnding) > new Date();
        const hadConflicts = prevStatus.knownConflicts && prevStatus.knownConflicts.length > 0;
        const hadResults = prevResults.length > 0;
        const wasRed = prevStatus.statusCode === 'r' || prevStatus.statusCode === 'R';
        const hasNoResults = currResults.length === 0;
        if (
          // The interval had results, conflicts, and was red in the previous state.
          hadResults &&
          hadConflicts &&
          wasRed &&
          // The interval now has no results. (Even if there are conflicts, they're moot even if they were never "resolved".)
          hasNoResults &&
          // The interval is in the future.
          isFuture
        ) {
          dispatch(
            updateObjective({
              uuid: objective.uuid,
              updates: {
                statuses: [
                  ...objective.statuses.filter(
                    (s) => s.forIntervalEnding !== currStatus.forIntervalEnding,
                  ),
                  {
                    ...currStatus,
                    statusCode: 'p',
                    knownConflicts: [],
                    lastUpdatedDate: new Date().toISOString(),
                  },
                ],
              },
            }),
          );
        }
      });
    });

    // Save a deep copy of objectives for next comparison
    prevObjectivesRef.current = cloneDeep(allObjectives);
  }, [currentDependencies, onConflictDetected, allObjectives, dispatch]);

  return {
    currentDependencies,
    lastKnownDependencies: lastKnownDependenciesRef.current,
    hasChanges: !isEqual(currentDependencies, lastKnownDependenciesRef.current),
  };
}
